import clsx from 'clsx';
import { memo } from 'react';

import '../../styles/index.css';
import type { IconGlyphSize, IconType } from '../../types/SharedIconProps';

import { icons } from '../../iconography/iconography';
import './Icon.css';
import { getValidIconKey } from './utils';

export type IconColor =
  | 'inherit'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'gray';

type IconProps = {
  /** Semantic name. */
  icon: IconType;
  /** Test hook rendered on the svg. */
  'data-testid'?: string;
  /** Internal fill emphasis. @default false */
  filled?: boolean;
  /** `inherit` uses the parent's color; otherwise one of the standard icon colors. @default 'inherit' */
  color?: IconColor;
  /** Accessible name. */
  label?: string;
  /** Layout size. @default 'm' */
  size?: IconGlyphSize;
};

export const Icon = memo((props: IconProps) => {
  const {
    icon,
    label,
    size = 'm',
    filled = false,
    color = 'inherit',
    'data-testid': testId,
  } = props;

  const iconKey = getValidIconKey(icon);
  if (iconKey === null) {
    return null;
  }

  const iconEntry = icons[iconKey];
  const className = clsx(
    'eds-icon',
    `eds-icon-size-${size}`,
    `eds-icon--${icon}`,
    color !== 'inherit' && `eds-icon--${color}`,
  );
  const a11yProps = {
    'data-testid': testId,
    'aria-hidden': label === undefined ? true : undefined,
    'aria-label': label,
    role: label !== undefined ? ('img' as const) : undefined,
    focusable: 'false' as const,
  };

  if (iconEntry.type === 'custom') {
    const Svg =
      filled && iconEntry.filled !== undefined
        ? iconEntry.filled
        : iconEntry.component;
    return (
      <Svg className={className} viewBox={iconEntry.viewBox} {...a11yProps} />
    );
  }

  const Svg =
    filled && iconEntry.filled !== undefined
      ? iconEntry.filled
      : iconEntry.base;

  return (
    <Svg
      className={className}
      viewBox={iconEntry.viewBox}
      fill="currentColor"
      {...a11yProps}
    />
  );
});

Icon.displayName = 'Icon';
